import {
  efficiencyCategories,
  EfficiencyCategory,
  FilterDimension,
  metaEfficiencyCategories,
  MetaEfficiencyCategory,
} from './schemas';

import type { Platform } from '@/features/users';

export const effiencyCategoriesLabels: Record<EfficiencyCategory, string> = {
  ad_format: 'Ad Format',
  browser: 'Browser Name',
  device_make: 'Device Make',
  device_type: 'Device Type',
  os_name: 'OS Name',
  region: 'Region',
  site: 'Website',
  supply_vendor: 'Supply Vendor',
};

export const metaEfficiencyCategoriesLabels: Record<
  MetaEfficiencyCategory,
  string
> = {
  gender: 'Gender',
  device: 'Device',
  age: 'Age',
  region: 'Region',
  platform: 'Platform',
  placement: 'Placement',
};

export interface StarvationBidderPlatformConfig {
  efficiencyCategories: readonly FilterDimension[];
  defaultEfficiencyCategory: FilterDimension;
  getCategoryLabel: (category: FilterDimension) => string;
  insightsAdUnitIdParamKey: 'adgroupId' | 'adsetId';
}

export const starvationBidderPlatformConfig: Record<
  Platform['name'],
  StarvationBidderPlatformConfig
> = {
  'The Trade Desk': {
    efficiencyCategories: efficiencyCategories,
    defaultEfficiencyCategory: 'site',
    getCategoryLabel: (category) =>
      effiencyCategoriesLabels[category as EfficiencyCategory],
    insightsAdUnitIdParamKey: 'adgroupId',
  },
  DV360: {
    efficiencyCategories: efficiencyCategories,
    defaultEfficiencyCategory: 'site',
    getCategoryLabel: (category) =>
      effiencyCategoriesLabels[category as EfficiencyCategory],
    insightsAdUnitIdParamKey: 'adgroupId',
  },
  Meta: {
    efficiencyCategories: metaEfficiencyCategories,
    defaultEfficiencyCategory: metaEfficiencyCategories[0],
    getCategoryLabel: (category) =>
      metaEfficiencyCategoriesLabels[category as MetaEfficiencyCategory],
    insightsAdUnitIdParamKey: 'adsetId',
  },
  TikTok: {
    efficiencyCategories: efficiencyCategories,
    defaultEfficiencyCategory: 'site',
    getCategoryLabel: (category) =>
      effiencyCategoriesLabels[category as EfficiencyCategory],
    insightsAdUnitIdParamKey: 'adgroupId',
  },
};

export const getStarvationBidderPlatformConfig = (
  platform: Platform['name']
): StarvationBidderPlatformConfig => starvationBidderPlatformConfig[platform];

export const getEfficiencyCategoriesForPlatform = (
  platform: Platform['name']
) => getStarvationBidderPlatformConfig(platform).efficiencyCategories;

export const getDefaultEfficiencyCategory = (platform: Platform['name']) =>
  getStarvationBidderPlatformConfig(platform).defaultEfficiencyCategory;

export const isValidEfficiencyCategoryForPlatform = (
  category: string,
  platform: Platform['name']
): category is FilterDimension =>
  (getEfficiencyCategoriesForPlatform(platform) as readonly string[]).includes(
    category
  );

export const getEfficiencyCategoryLabel = (
  category: FilterDimension,
  platform: Platform['name']
) => getStarvationBidderPlatformConfig(platform).getCategoryLabel(category);
